import { useCallback } from "react";
import type { ShoppingList, ShoppingSettings } from "@shared/types";
import { shoppingListRepository, shoppingSettingsRepository } from "@data/firestore";
import { useProjectDoc } from "@data/hooks/useProjectDoc";
import {
  totals as computeTotals,
  materialsByCategory,
} from "@domain/shopping/shoppingList";

/**
 * The active project's owner-sourced line items + tax setting. Shared by
 * the Plan page (itemizing materials) and the Shopping List page
 * (purchasing view), so it lives in the data layer rather than a feature.
 */
export function useShoppingList() {
  const [items, setItems, loaded] = useProjectDoc<ShoppingList>(shoppingListRepository);
  const [settings, setSettings] = useProjectDoc<ShoppingSettings>(shoppingSettingsRepository);
  const taxRate = Number(settings?.salesTaxRate) || 0;

  const setTaxRate = useCallback(
    (rate: number) => setSettings((prev) => ({ ...prev, salesTaxRate: rate })),
    [setSettings]
  );

  return {
    items,
    setItems,
    loaded,
    taxRate,
    setTaxRate,
    totals: computeTotals(items, taxRate),
    byCategory: materialsByCategory(items, taxRate),
  };
}
